import { useContext, useMemo, useState } from 'react'

import { LanguageContext } from '@lib/language-context'
import { ProductContext } from '@lib/product/context'
import { useProductPrice } from '@lib/product/price'
import { getPageUrl } from '@lib/routes'
import { useProductCartAttributes } from '@lib/shopify/attribute'
import { StringsContext } from '@lib/strings-context'
import { UserContext } from '@lib/user/context'
import { getHasUserPurchasePermission } from '@lib/user/helpers'

import ProductActions from '@blocks/product/product-actions'
import ProductActionsGuest from '@blocks/product/product-actions-guest'
import ProductGallery from '@blocks/product/product-gallery'
import ProductPrice from '@blocks/product/product-price'
import SimplePortableText from '@components/simple-portable-text'
import ProductStock from '@blocks/product/product-stock'
import ProductOptionsSelect from '@blocks/product/product-options-select'

const ProductHero = () => {
  const { locale } = useContext(LanguageContext)
  const { product, activeVariant, onVariantChange } = useContext(ProductContext)
  const strings = useContext(StringsContext)
  const { user } = useContext(UserContext)

  const productCartAttributes = useProductCartAttributes(product)
  const [price, comparePrice] = useProductPrice(product, activeVariant)

  const [previewVariantId, setPreviewVariantId] = useState<number | null>(null)

  const previewVariant = useMemo(() => {
    return product?.variants?.find(
      (variant) => variant.variantID === previewVariantId,
    )
  }, [previewVariantId, product?.variants])

  if (!product || !activeVariant || !onVariantChange) {
    return null
  }

  const hasUserPurchasePermission = getHasUserPurchasePermission(user)
  const hasPrice = typeof price !== 'undefined' && hasUserPurchasePermission
  const hasActions = !!user && hasUserPurchasePermission
  const hasGuestActions = !!user && !hasUserPurchasePermission

  const productPageUrl = getPageUrl({
    locale,
    pageType: 'product',
    slugs: product.slug.current,
  })

  return (
    <section className="container grid grid-cols-1 md:grid-cols-2 gap-8 py-12">
      <ProductGallery
        photosets={product.galleryPhotos ?? []}
        activeVariant={activeVariant}
        previewVariant={previewVariant}
        preloadFirstImage
        hasDrag
        hasThumbnails
      />

      <div className="flex flex-col w-full md:max-w-[620px] md:ml-auto gap-y-[1.875rem]">
        <div className="space-y-2.5">
          <h1 className="is-h2">{product.title}</h1>
          {hasPrice && (
            <ProductPrice
              size="lg"
              price={price}
              comparePrice={comparePrice}
              showPrice
              showPriceTaxSuffix
              showCurrency
            />
          )}
          <div className="flex items-start gap-x-5 gap-y-2.5">
            <span className="text-sm">
              {strings.productSkuLabel} {activeVariant.sku}
            </span>
            <ProductStock productVariant={activeVariant} size="sm" />
          </div>
        </div>

        <ProductOptionsSelect
          product={product}
          activeVariant={activeVariant}
          onChange={(variant) => {
            onVariantChange(variant.variantID)
          }}
          onPreview={(variant) => {
            setPreviewVariantId(variant?.variantID ?? null)
          }}
        />

        {hasActions && (
          <ProductActions
            product={product}
            variantIds={[activeVariant.variantID]}
            inStock={activeVariant.inStock}
            cartAttributes={productCartAttributes}
          />
        )}

        {hasGuestActions && <ProductActionsGuest returnTo={productPageUrl} />}

        {!!product.description && (
          <SimplePortableText className="rc" content={product.description} />
        )}
      </div>
    </section>
  )
}

export default ProductHero
